/*
 * ArticleTemplate (entry): monta a página de conteúdo sobre o SSR que o PHP entregou.
 *
 * O contrato com `Astrolink/website/artigo.php`:
 *
 *   <article-template class="content-page__grid article-format">
 *       <article-head>   …breadcrumb, h1, descrição, infos, tempo de leitura, imagem…
 *       <article-body>   …o corpo do CMS…
 *   </article-template>
 *   <script>window.__AL_ARTICLE__ = { … }</script>
 *
 * Só essas duas regiões têm conteúdo no HTML servido: é o artigo, e é o que os buscadores
 * leem. O resto do grid é montado aqui.
 *
 * COMO O ARTIGO ENTRA NO REACT: o render LIMPA o container, então os nós do SSR são
 * retirados antes, para dentro de um `DocumentFragment`, e devolvidos pelo `AdoptedRegion` no
 * `useLayoutEffect`. São os MESMOS nós: nada é reserializado, e os 18 KB de HTML do CMS não
 * passam duas vezes pelo parser.
 *
 * A REDE DE SEGURANÇA existe porque essa troca é a única coisa nesta página que pode fazer o
 * artigo sumir. Se o render estourar antes dos efeitos de layout, o fragmento do corpo ainda
 * está cheio, e é isso que se testa depois. Nesse caso o `<article-template>` é remontado
 * como veio do servidor: perde-se o cromo, nunca o conteúdo. O caso "o bundle nem carregou"
 * não precisa de rede: sem executar, nada foi retirado.
 *
 * POR QUE `flushSync`: o `createRoot().render()` do React 18 agenda o trabalho em vez
 * de commitar na hora. Sem forçar o flush, a checagem do fragmento (e o `catch`) rodariam ANTES
 * dos efeitos de layout, e a rede de segurança dispararia sempre e desfaria a montagem. O
 * `flushSync` devolve a sincronia que o `ReactDOM.render` da v17 dava aqui.
 */
import React from 'react';

import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import { whenModuleCssReady } from '../../../services/ModuleCss';

import ContentTemplate from '../../components/container/ArticleTemplate';
import { TAdoptedRegions } from '../../components/container/ArticleTemplate/types';

import { getArticleConfig } from './articleConfig';

const MODULE_CSS = ['articletemplate.module.css', 'articles.container.css'];

const ADOPTED_REGION_TAGS: [string, string][] = [
    ['risingPositions', 'article-rising-positions'],
    ['planetInSigns', 'article-planet-in-signs'],
    ['planetInHouses', 'article-planet-in-houses'],
    ['signsOnAstrolink', 'article-signs-on-astrolink'],
    ['userPosition', 'article-user-position']
];

/** Esvazia o elemento do SSR num fragmento, preservando a identidade de cada nó. */
const drain = (element: Element | null): DocumentFragment | null => {
    if (!element) return null;

    const fragment = document.createDocumentFragment();

    while (element.firstChild) fragment.appendChild(element.firstChild);

    return fragment;
};

const mountArticle = (): void => {
    const host = document.querySelector('article-template');

    if (!host) return;

    const data = getArticleConfig();

    if (!data) return;

    const headElement = host.querySelector('article-head');
    const bodyElement = host.querySelector('article-body');

    const head = drain(headElement);
    const body = drain(bodyElement);

    const regions = ADOPTED_REGION_TAGS.map(([key, tag]) => {
        const element = host.querySelector(tag);

        return { key, element, content: drain(element) };
    });

    const adopted = regions.reduce<TAdoptedRegions>((accumulated, region) => ({
        ...accumulated,
        [region.key]: region.content
    }), {});

    /** Devolve a página ao estado em que o servidor a entregou. */
    const restore = (): void => {
        host.innerHTML = '';

        if (headElement && head) {
            headElement.appendChild(head);
            host.appendChild(headElement);
        }

        if (bodyElement && body) {
            bodyElement.appendChild(body);
            host.appendChild(bodyElement);
        }

        regions.forEach(({ element, content }) => {
            if (!element || !content) return;

            element.appendChild(content);
            host.appendChild(element);
        });
    };

    try {
        flushSync(() => {
            createRoot(host).render(
                <ContentTemplate
                    variation={data.variation}
                    data={data}
                    head={head}
                    body={body}
                    adopted={adopted}
                />
            );
        });
    } catch (error) {
        restore();
        throw error;
    }

    // fragmento ainda cheio = os efeitos de layout não rodaram e o artigo não foi devolvido
    if (body && body.childNodes.length) restore();
};

Promise.all(MODULE_CSS.map((key) => whenModuleCssReady(key))).then(mountArticle);
